import { Modal, Button, Spinner } from 'react-bootstrap';
import { FiTrash2, FiAlertTriangle, FiX } from 'react-icons/fi';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { IOrg } from '../../models/org';
import { deleteOrg } from '../../api/org';

type Props = {
	org: IOrg;
	cancel: React.Dispatch<React.SetStateAction<boolean>>;
	setError: React.Dispatch<React.SetStateAction<string>>;
}

const ConfirmDeleteOrg = ({org, cancel, setError} : Props) => {
	const handleClose = () => cancel(false);
	const navigate = useNavigate();

	const queryClient = useQueryClient();
	const { mutateAsync: deleteOrgFn, isLoading} = useMutation(
		deleteOrg,
		{
			onSuccess: () => {
				queryClient.invalidateQueries(['Organizations']);
				navigate("/dashboard/orgs");
			},
			onError: (error: any) => {
				if(error?.response?.data?.name === "RestoreConfirmationRequired") {
					setError("You cannot delete this organization because it has agents associated with it. Please delete the agents first.");
				} else {
					setError(
						error?.response?.data?.message ||
						"Unexpected error. Please try again or contact admin."
					);
				}
				
				handleClose();
				return;
			},
		}
	);

	const handleDelete = async () => {
		if(!org) {
			setError("Organization not found.");
			handleClose();
			return;
		}
		deleteOrgFn(org.id);
	};

	return (
		<Modal 
			show={!!org} 
			onHide={handleClose} 
			size="lg" 
			centered
			className="confirm-delete-modal"
		>
			<Modal.Header 
				closeButton 
				className="bg-dark border-danger"
			>
				<Modal.Title className="text-danger d-flex align-items-center">
					<FiTrash2 className="me-2" size={20} />
					Delete Organization
				</Modal.Title>
			</Modal.Header>
			
			<Modal.Body className="bg-dark text-light">
				<div className="text-center mb-4">
					<div className="danger-icon mb-3">
						<FiAlertTriangle size={48} className="text-danger" />
					</div>
					
					<h5 className="text-light mb-3">
						Are you absolutely sure?
					</h5>
					
					<p className="mb-4" style={{ color: '#ccc' }}>
						You are about to permanently delete the organization{' '}
						<strong className="text-danger">"{org.name}"</strong>.
					</p>
				</div>

				<div className="warning-box p-3 mb-4 border border-danger rounded">
					<div className="d-flex align-items-start">
						<FiAlertTriangle className="text-danger me-3 mt-1" size={20} />
						<div>
							<h6 className="text-danger mb-2">This action cannot be undone</h6>
							<ul className=" mb-0 small" style={{ color: '#ccc' }}>
								<li>All agents and their configurations will be permanently deleted</li>
								<li>All uploaded data and knowledge base content will be lost</li>
								<li>All conversation history will be permanently removed</li>
								<li>All team member access will be revoked</li>
								<li>Current Subscription(s) for this organization will be lost</li>
							</ul>
						</div>
					</div>
				</div>

				<div className="confirmation-text p-3 bg-secondary bg-opacity-25 rounded">
					<p className="mb-2 small" style={{ color: '#ccc' }}>
						To confirm deletion, please verify you want to delete:
					</p>
					<p className="mb-0 fw-bold text-light">
						{org.name}
					</p>
				</div>
			</Modal.Body>
			
			<Modal.Footer className="bg-dark border-danger">
				<Button 
					variant="outline-secondary" 
					onClick={handleClose}
					disabled={isLoading}
					className="d-flex align-items-center gap-2"
				>
					<FiX size={16} />
					Cancel
				</Button>
				
				<Button 
					variant="danger" 
					onClick={handleDelete}
					disabled={isLoading}
					className="d-flex align-items-center gap-2"
				>
					{isLoading ? (
						<>
							<Spinner
								as="span"
								animation="border"
								size="sm"
								role="status"
								aria-hidden="true"
							/>
							Deleting...
						</>
					) : (
						<>
							<FiTrash2 size={16} />
							Delete Organization
						</>
					)}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default ConfirmDeleteOrg;